import type { TextProps } from '@mantine/core';
import { Group, Text, useMantineTheme } from '@mantine/core';
import { memo, useMemo } from 'react';

import { Warning } from '@apple/assets/icons';
import { useTranslation } from '@apple/lib/i18next';

type Props = Omit<TextProps, 'c' | 'span' | 'children'> & {
	status: string;
	isOrderMarkedForCancellation?: boolean;
};

export const OrderStatusDisplay = memo(
	({ status, isOrderMarkedForCancellation = false, ...textProps }: Props) => {
		const { t } = useTranslation('order');
		const theme = useMantineTheme();
		const statusColor = useMemo(
			() => theme.other.getOrderStatusColor(status, isOrderMarkedForCancellation, t),
			[theme.other, status, isOrderMarkedForCancellation, t],
		);

		return (
			<Group wrap='nowrap' align='center' justify='flex-start' gap={0}>
				<Text c={statusColor} span {...textProps}>
					{status}
				</Text>
				{isOrderMarkedForCancellation || status === t('orderStatus.orderReview') ? (
					<Warning
						size={textProps.size}
						c='yellow.7'
						tooltip={
							isOrderMarkedForCancellation
								? t('statusTooltips.cancellationRequested')
								: t('statusTooltips.orderReview')
						}
					/>
				) : null}
			</Group>
		);
	},
);
OrderStatusDisplay.displayName = 'OrderStatusDisplay';
